/**
 * TokenManager is a utility class that manages a token and refreshes it when needed.
 * It can be used by multiple callers in parallel.
 */
export class TokenManager {
    private token: string | undefined = undefined;
    private tokenPromise: Promise<string> | undefined = undefined;

    /**
     * Creates a new instance of TokenManager.
     * @param tokenGetter Function that returns a token promise from external source. It is called only when token refresh is needed.
     */
    constructor(private readonly tokenGetter: () => Promise<string>) { }
    
    /**
     * Gets the current token or refreshes it if does not exist.
     * It does not auto refresh token. To refresh token call {@link refreshToken}.
     * @returns Token promise.
     */
    public getToken(): Promise<string> {
        return this.token !== undefined
            ? Promise.resolve(this.token)
            : this.refreshToken();
    }

    /**
     * Refreshes token and returns a promise with the new token.
     * It's multithreaded - if there is already a token refresh in progress, it returns the same promise.
     * @returns Token promise.
     */
    public refreshToken(): Promise<string> {
        if (this.tokenPromise === undefined) {
            this.token = undefined;
            this.tokenPromise = this.tokenGetter().then((token) => {
                this.token = token;
                this.tokenPromise = undefined;
                return token;
            });
        }

        return this.tokenPromise;
    }
}
